import {computed, inject, Injectable, signal, WritableSignal} from "@angular/core";
import {FieldItem, FieldValue, FieldValues, ObjectView, ViewField, ViewSection} from "../core/definitions/object-view";
import {MetaField} from "../core/definitions/meta-field";
import {Section, SectionsContainer} from "../core/definitions/sections-container";
import {FieldInputType} from "../core/definitions/field-input-type.enum";
import {FieldParameters} from "../core/definitions/field-parameters";
import {IfType} from "../core/definitions/field-if";
import {FieldConditionService} from "../core/field-condition.service";
import {SuperObjectModel} from "../core/definitions/super-object-model";
import {ObjectViewDataParams, ObjectViewDataService} from "../core/object-view-data.service";
import {ModelSchema} from "../core/definitions/model-schema";
import {Setting} from "../core/definitions/setting";
import {Inline} from "../core/definitions/inline";
import {FieldType} from "../core/definitions/field-type.enum";
import {TranslateService} from "@ngx-translate/core";

const MEDIA_SECTION_NAME = 'copy_media';
const MEDIA_FIELDS = {
  'images': {title: 'TRANS__SEARCH_VIEWS__VIEW__IMAGES', model: 'ImageItem', idField: 'image_id'},
  'audios': {title: 'TRANS__SEARCH_VIEWS__VIEW__AUDIO', model: 'AudioItem', idField: 'audio_id'},
  'videos': {title: 'TRANS__SEARCH_VIEWS__VIEW__VIDEOS', model: 'VideoItem', idField: 'video_id'},
  'models_3d': {title: 'TRANS__SEARCH_VIEWS__VIEW__MODEL_3D', model: 'Model3DItem', idField: 'model_3d_id'},
  'attachments': {title: 'TRANS__SEARCH_VIEWS__VIEW__ATTACHMENTS', model: 'AttachmentItem', idField: 'attachment_id'},
};

@Injectable()
export class ObjectCopyService {
  private readonly fieldConditionService: FieldConditionService = inject(FieldConditionService);
  private readonly objectViewDataService: ObjectViewDataService = inject(ObjectViewDataService);
  private readonly translate: TranslateService = inject(TranslateService);

  sectionsContainer: WritableSignal<SectionsContainer> = signal(null);
  objectViewData: WritableSignal<ObjectView> = signal(null);
  selectedFields: WritableSignal<string[]> = signal([]);

  private isFieldRequiredFn: (field: MetaField) => boolean = null;
  private hideRequiredFields: boolean = true;

  initializeCopyData(
    container: SectionsContainer,
    sourceArtifactId: string,
    isFieldRequiredFn?: (field: MetaField) => boolean,
    hideRequiredFields: boolean = true
  ): void {
    this.sectionsContainer.set(null);
    this.objectViewData.set(null);
    this.selectedFields.set([]);

    const objectViewDataParams = new ObjectViewDataParams();
    objectViewDataParams.objectId = sourceArtifactId;

    this.objectViewDataService.createViewData(objectViewDataParams).then(viewData => {
      this.sectionsContainer.set(container);
      this.objectViewData.set(viewData);
    });

    // Optional function that evaluates whether a field is required or not
    this.isFieldRequiredFn = isFieldRequiredFn ?? null;
    this.hideRequiredFields = hideRequiredFields;

    // Add synthetic media sections based on the root object's media fields
    const mediaEditFields = Object.entries(MEDIA_FIELDS)
      .filter(([name]) => container.rootObject?.[name]?.length)
      .map(([name, {title, model, idField}]) => ({
        key: name,
        name,
        title,
        field_type: FieldType.ARRAY,
        input_type: FieldInputType.INLINE_ARRAY,
        inline: {model, prop: idField, unique_props: [idField]} as Inline,
      } as MetaField));

    if (mediaEditFields.length) {
      container.sections = container.sections.filter(section => !section.name.startsWith(MEDIA_SECTION_NAME));
      container.sections.push({
        name: MEDIA_SECTION_NAME,
        title: 'TRANS__COPY_FIELDS__MEDIA_TITLE',
        fields: mediaEditFields,
        editFields: mediaEditFields,
      } as Section);
    }

    // Mark required fields as selected, otherwise reset `keep` flags
    container.sections.flatMap(section => section.editFields).forEach(field => {
      this.toggleField(this.getIsFieldRequired(field), field);
    });
  }

  rootObject = computed<SuperObjectModel>(() =>
    this.sectionsContainer()?.rootObject);

  sections = computed<Section[]>(() =>
    this.sectionsContainer()?.sections ?? []);

  contextFields = computed<MetaField[]>(() =>
    this.sections().flatMap(section => section.fields)
      .filter(field => field.input_type === FieldInputType.CONTEXT));

  copyableSections = computed<Section[]>(() =>
    this.sections().filter(section => this.filterCopyableFields(section).length > 0));

  getViewSection(section: Section): ViewSection | null {
    if (!this.objectViewData()?.sections) {
      return null;
    }
    return this.objectViewData().sections.find(s => s.name === section.name);
  }

  getViewField(section: Section, field: MetaField): ViewField | null {
    if (field && this.getIsMediaField(section, field)) {
      const mediaField = MEDIA_FIELDS[field.key];
      const mediaItems = [];
      for (const item of this.getFieldValue(field) ?? []) {
        mediaItems.push({
          field_values: {
            values: [{
              value: item[mediaField.idField + '_value'],
              ref_id: item[mediaField.idField],
              ref_object_type: mediaField.model,
            } as FieldValue],
          } as FieldValues,
        } as FieldItem);
      }
      return {
        field_name: field.name,
        field_type: 'array',
        title: this.translate.instant(field.title),
        items: mediaItems,
      } as ViewField
    }

    const viewSection = this.getViewSection(section);
    if (!viewSection) {
      return null;
    }
    let fieldPath = null;
    if (field.input_type === 'inline') {
      fieldPath = field.key + '.' + field.inline.prop;
    } else {
      fieldPath = field.key.split('->').join('.');
    }
    return (fieldPath && viewSection?.section_fields?.find(f => f.field_name === fieldPath)) ?? null;
  }

  filterCopyableFields(section: Section): MetaField[] {
    return section.editFields.filter(field =>
      this.checkCanCopyField(section, field) &&
      (!this.hideRequiredFields || !this.getIsFieldRequired(field))
    );
  }

  getFieldRootValue(field: MetaField): any | null {
    const rootObject = this.sectionsContainer().rootObject;
    if (!rootObject) {
      return null;
    }

    const key = field.key.split('->');

    return rootObject[key[0]] ?? null;
  }

  getFieldValue(field: MetaField): any | null {
    const rootObject = this.sectionsContainer().rootObject;
    if (!rootObject) {
      return null;
    }
    const keys = field.key.split('->');
    let value = rootObject;

    for (const key of keys) {
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        value = value[key];
      } else {
        return null;
      }
    }
    return value;
  }

  checkCanCopyField(section: Section, field: MetaField): boolean {
    if (section.name.startsWith(MEDIA_SECTION_NAME)) {
      return true;
    }
    const fieldValue = this.getFieldValue(field);
    if (!this.getViewField(section, field) || !fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0)) {
      return false;
    }
    const fieldParameters = new FieldParameters();
    fieldParameters.sectionsContainer = this.sectionsContainer();
    fieldParameters.rootObject = this.sectionsContainer().rootObject;
    fieldParameters.object = this.sectionsContainer().rootObject;
    fieldParameters.edit = false;
    fieldParameters.field = field;
    return this.fieldConditionService.runIf(IfType.SHOW, fieldParameters).result;
  }

  createSuperObjectModel(
    sectionsContainer: SectionsContainer,
    sourceArtifactId: string,
    schema?: ModelSchema,
    settings?: Setting[],
  ): SuperObjectModel {
    const targetIdentifierContext =
      sectionsContainer.useContextCreatingIdentifier && sectionsContainer.operationContextObjects.length
        ? sectionsContainer.operationContextObjects[0].artifact_id
        : sourceArtifactId;
    let newObject = new SuperObjectModel();

    for (const section of sectionsContainer.sections) {
      for (const field of section.fields) {
        if (this.getIsFieldSelected(field) || field.is_required === 'is_required') {
          let targetValue = this.getFieldRootValue(field);

          if (Object.keys(MEDIA_FIELDS).includes(field.key) && Array.isArray(targetValue) && field?.inline?.prop) {
            const keepMediaItemIds = field.value ? field.value.split(',') : [];
            targetValue = targetValue.filter(item => keepMediaItemIds.includes(item[field.inline.prop]));
          }

          newObject[field.key.split('->')[0]] = targetValue;
        }
      }
    }
    let regLevel = null;

    if (schema && settings) {
      regLevel = this.getDefaultRegLevel(settings, schema);
    }

    if (regLevel && newObject.hasOwnProperty('reg_level')) {
      newObject['reg_level']['registration_level_id'] = regLevel;
      newObject['reg_level']['registration_level_id_value'] = '';
    }

    newObject.identification_number = {
      auto_generated: false,
      identification_number: '_IGNORE_',
      // @ts-ignore
      context_id: targetIdentifierContext,
    };
    newObject.object_type = sectionsContainer.rootObject.object_type;
    newObject.superobject_type_id = sectionsContainer.rootObject.superobject_type_id;
    newObject.superobject_type_id_value = sectionsContainer.rootObject.superobject_type_id_value;
    newObject.create_multiple = false;

    return newObject;
  }

  getDefaultRegLevel(settings: Setting[], schema: ModelSchema) {
    let definitionKey = '';
    let defaultRegLevel = null;

    switch (schema.title) {
      case 'Architecture': {
        definitionKey = schema.$defs.RegistrationLevelArchitecture.properties.registration_level_id['default'].replaceAll('DEF_', '');
        break;
      }

      case 'Artwork': {
        definitionKey = schema.$defs.RegistrationLevelArtwork.properties.registration_level_id['default'].replaceAll('DEF_', '');
        break;
      }

      case 'Building': {
        definitionKey = schema.$defs.RegistrationLevelBuilding.properties.registration_level_id['default'].replaceAll('DEF_', '');
        break;
      }

      case 'Design': {
        definitionKey = schema.$defs.RegistrationLevelDesign.properties.registration_level_id['default'].replaceAll('DEF_', '');
        break;
      }

      case 'Naval Vessel': {
        definitionKey = schema.$defs.RegistrationLevelNavalVessel.properties.registration_level_id['default'].replaceAll('DEF_', '');
        break;
      }

      case 'Thing': {
        definitionKey = schema.$defs.RegistrationLevelThing.properties.registration_level_id['default'].replaceAll('DEF_', '');
        break;
      }

      case 'Photograph': {
        definitionKey = schema.$defs.RegistrationLevelPhotograph.properties.registration_level_id['default'].replaceAll('DEF_', '');
        break;
      }
    }

    for (const setting of settings) {
      if (setting.sub_settings.length) {
        let def = setting.sub_settings.find(s => s.key === definitionKey);

        if (def) {
          defaultRegLevel = def.value;
        }
      }
    }

    return defaultRegLevel;
  }

  toggleSection(selected: boolean, section: Section): void {
    this.filterCopyableFields(section).forEach(field => {
      this.toggleField(selected, field);
    });
  }

  toggleSections(selected: boolean, sections: Section[]): void {
    sections.forEach(section => {
      this.toggleSection(selected, section);
    });
  }

  getIsSectionSelected(section: Section): boolean {
    return this.filterCopyableFields(section).every(field =>
      this.getIsFieldSelected(field));
  }

  getIsSectionIndeterminate(section: Section): boolean {
    const copyableFields = this.filterCopyableFields(section);
    const selectedFields = copyableFields.filter(field =>
      this.getIsFieldSelected(field));
    return selectedFields.length > 0 && selectedFields.length < copyableFields.length;
  }

  toggleField(selected: boolean, field: MetaField): void {
    this.selectedFields.update(oldState => {
      if (selected && !oldState.includes(field.key)) {
        return [...oldState, field.key];
      }
      if (!selected && oldState.includes(field.key)) {
        return oldState.filter(fKey => fKey !== field.key);
      }
      return oldState;
    });

    field.keep = selected;
  }

  getIsMediaField(section: Section, field: MetaField): boolean {
    return section.name === MEDIA_SECTION_NAME && Object.keys(MEDIA_FIELDS).includes(field.key);
  }

  getIsFieldSelected(field: MetaField): boolean {
    return this.getIsFieldRequired(field) || this.selectedFields().includes(field.key);
  }

  getIsFieldRequired(field: MetaField): boolean {
    return this.isFieldRequiredFn ? this.isFieldRequiredFn(field) : false;
  }
}
